import React from 'react';
import { RuleGroupCard } from '../../common/Cards';
import { createEmptyRuleGroup } from '../../../utils/strategyUtils';

// SVG Icons
const AddIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);

/**
 * TradingRulesSection - Stacked vertical layout for Entry and Exit rules
 * 
 * @param {Object} props
 * @param {Array} props.ruleGroups - All rule groups
 * @param {Function} props.onUpdateGroup - Update handler (groupIndex, updatedGroup) => void
 * @param {Function} props.onAddGroup - Add group handler (action) => void
 * @param {Function} props.onDeleteGroup - Delete group handler (groupIndex) => void
 * @param {Function} props.onDuplicateGroup - Duplicate group handler (groupIndex) => void
 * @param {Function} props.onAddCondition - Add condition handler (groupIndex, conditionType) => void
 * @param {Function} props.onEditCondition - Edit condition handler (groupIndex, conditionIndex) => void
 * @param {Function} props.onDeleteCondition - Delete condition handler (groupIndex, conditionIndex) => void
 * @param {boolean} props.disabled - Whether editing is disabled
 * @param {Object} props.rulesSchema - Rules schema from API
 */
const TradingRulesSection = ({
    ruleGroups = [],
    onUpdateGroup,
    onAddGroup,
    onDeleteGroup,
    onDuplicateGroup,
    onAddCondition,
    onEditCondition,
    onDeleteCondition,
    disabled = false,
    rulesSchema = null,
}) => {
    // Separate groups by action
    const entryGroups = ruleGroups
        .map((group, index) => ({ group, originalIndex: index }))
        .filter(({ group }) => group.action === 'BUY');
    
    const exitGroups = ruleGroups
        .map((group, index) => ({ group, originalIndex: index }))
        .filter(({ group }) => group.action === 'SELL');

    // Count total conditions
    const entryConditionCount = entryGroups.reduce(
        (sum, { group }) => sum + (group.conditions?.length || 0), 0
    );
    const exitConditionCount = exitGroups.reduce(
        (sum, { group }) => sum + (group.conditions?.length || 0), 0
    );

    const renderGroupSection = (groups, action, accentColor, label, conditionCount) => {
        const isEntry = action === 'BUY';
        const bgClass = isEntry ? 'bg-success/5' : 'bg-error/5';
        const accentClass = isEntry ? 'bg-success' : 'bg-error';
        const textClass = isEntry ? 'text-success' : 'text-error';
        const badgeClass = isEntry ? 'badge-success' : 'badge-error';

        return (
            <div className={`${bgClass} rounded-lg relative`}>
                {/* Accent bar at top */}
                <div className={`absolute top-0 left-0 w-full h-1 ${accentClass} rounded-t-lg`}></div>
                
                {/* Section Header */}
                <div className="flex items-center justify-between p-4 pt-5">
                    <h4 className={`font-bold ${textClass} flex items-center gap-2`}>
                        {label} Rules
                        <span className={`badge ${badgeClass} badge-sm badge-outline`}>
                            {isEntry ? 'BUY' : 'SELL'}
                        </span>
                    </h4>
                    <span className="text-sm text-base-content/60">
                        {conditionCount} condition{conditionCount !== 1 ? 's' : ''}
                    </span>
                </div>

                {/* Rule Groups */}
                <div className="px-4 pb-4 space-y-4">
                    {groups.length === 0 ? (
                        <div className="py-6 text-center bg-base-200 rounded-lg border border-dashed border-base-300">
                            <p className="text-base-content/60 mb-1">No {label.toLowerCase()} rules defined</p>
                            <p className="text-xs text-base-content/40">
                                Add a rule group to define {label.toLowerCase()} conditions
                            </p>
                        </div>
                    ) : (
                        groups.map(({ group, originalIndex }, idx) => (
                            <React.Fragment key={group.groupId || originalIndex}>
                                {/* OR separator between groups */}
                                {idx > 0 && (
                                    <div className="flex items-center justify-center py-2">
                                        <div className="flex-1 border-t border-base-300"></div>
                                        <span className="px-4 text-sm font-medium text-base-content/60 bg-base-100 rounded-full border border-base-300">
                                            OR
                                        </span>
                                        <div className="flex-1 border-t border-base-300"></div>
                                    </div>
                                )}
                                
                                {/* Rule Group Card */}
                                <div className="bg-base-100 rounded-lg shadow-sm">
                                    <RuleGroupCard
                                        ruleGroup={group}
                                        index={originalIndex}
                                        onUpdate={(_, updated) => onUpdateGroup(originalIndex, updated)}
                                        onDelete={groups.length > 1 ? () => onDeleteGroup(originalIndex) : null}
                                        onDuplicate={() => onDuplicateGroup(originalIndex)}
                                        onAddCondition={(_, type) => onAddCondition(originalIndex, type)}
                                        onEditCondition={(_, cIndex) => onEditCondition(originalIndex, cIndex)}
                                        onDeleteCondition={(_, cIndex) => onDeleteCondition(originalIndex, cIndex)}
                                        expanded={true}
                                        onToggleExpand={null}
                                        editable={!disabled}
                                        rulesSchema={rulesSchema}
                                    />
                                </div>
                            </React.Fragment>
                        ))
                    )}

                    {/* Add Group Button */}
                    <button
                        className="btn btn-ghost btn-sm w-full border border-dashed border-base-300 gap-2"
                        onClick={() => onAddGroup(action)}
                        disabled={disabled}
                    >
                        <AddIcon />
                        Add {label} Rule Group
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Section Header */}
            <div>
                <h2 className="text-lg font-bold mb-2">Trading Rules</h2>
                <p className="text-sm text-base-content/60">
                    Configure entry and exit conditions for your strategy. 
                    Multiple rule groups are combined with OR logic.
                </p>
            </div>

            {/* Entry Rules Section */}
            {renderGroupSection(entryGroups, 'BUY', 'success', 'ENTRY', entryConditionCount)}

            {/* Exit Rules Section */}
            {renderGroupSection(exitGroups, 'SELL', 'error', 'EXIT', exitConditionCount)}
        </div>
    );
};

export default TradingRulesSection;
